import {DynamicalDatasetDescriptor} from './datasets';

const STAC_ROOT = 'https://stac.dynamical.org';

/**
 * dynamical.org bumps the versioned store path (…/vX.Y.Z.icechunk/) without notice,
 * so the current URL must be discovered from the STAC collection at runtime rather
 * than hardcoded. Resolved URLs are cached for the session with a TTL so a long-lived
 * tab picks up a version bump eventually.
 */
const STORE_URL_TTL_MS = 6 * 60 * 60 * 1000;

type StacAsset = {
  href: string;
  ['xarray:storage_options']?: {client_kwargs?: {region_name?: string}};
};

const cache = new Map<string, {url: string; fetchedAt: number}>();

function s3ToHttps(href: string, region: string): string {
  const match = href.match(/^s3:\/\/([^/]+)\/(.+?)\/?$/);
  if (!match) {
    throw new Error(`Unexpected icechunk asset href: ${href}`);
  }
  return `https://${match[1]}.s3.${region}.amazonaws.com/${match[2]}`;
}

export async function getIcechunkStoreUrl(
  descriptor: DynamicalDatasetDescriptor,
  now: () => number = Date.now
): Promise<string> {
  const cached = cache.get(descriptor.datasetId);
  if (cached && now() - cached.fetchedAt < STORE_URL_TTL_MS) {
    return cached.url;
  }
  try {
    // Bounded: a stalled STAC connection would otherwise hang the whole query
    // (the pinned-URL fallback only engages on rejection).
    const response = await fetch(`${STAC_ROOT}/${descriptor.datasetId}/collection.json`, {
      signal:
        typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function'
          ? AbortSignal.timeout(10 * 1000)
          : undefined,
    });
    if (!response.ok) {
      throw new Error(`STAC collection fetch failed: ${response.status}`);
    }
    const collection = (await response.json()) as {assets?: {icechunk?: StacAsset}};
    const asset = collection.assets?.icechunk;
    if (!asset?.href) {
      throw new Error('STAC collection has no icechunk asset');
    }
    const region =
      asset['xarray:storage_options']?.client_kwargs?.region_name ?? 'us-west-2';
    const url = s3ToHttps(asset.href, region);
    cache.set(descriptor.datasetId, {url, fetchedAt: now()});
    return url;
  } catch (error) {
    console.warn(
      `Falling back to pinned store URL for ${descriptor.datasetId}:`,
      error
    );
    return descriptor.fallbackStoreUrl;
  }
}

export function clearStoreUrlCacheForTesting() {
  cache.clear();
}
